import { useMemo, useState } from "react";
import { ApiFailure } from "@/components/data-display/api-failure";
import { PageHeader } from "@/components/data-display/page-header";
import { Scene, Card, Lines, Dots } from "@/components/illustrations/illustration-kit";
import { buttonVariants } from "@/components/ui/button";
import { LoadingState } from "@/components/ui/system-state";
import {
  NotificationEmptyState,
  NotificationItem,
} from "@/features/notifications/notification-item";
import { useNotifications } from "@/hooks/use-notifications";
import { cn } from "@/lib/utils";

/**
 * Full notification history (NOT-00) — the page behind the bell panel's
 * "View all notifications" link. Same row component as the panel, plus the
 * All/Unread filter.
 *
 * Ported from `.design-ref/prototype/app/(enterprise-wallet)/notifications/page.tsx`.
 * The design read a zustand store seeded with sample notifications, with
 * "Clear all" and "Restore sample notifications" acting on that store. Neither
 * is carried over: there is no clear/delete route in the gateway, and restoring
 * sample rows would be exactly the fabricated-UI failure the page pipeline bans.
 * The page is backed only by `GET /v1/notifications` and
 * `POST /v1/notifications/{id}/read` (via `useNotifications()`); see
 * report.md → BACKEND GAPS.
 */
type Filter = "all" | "unread";

/** Empty-feed artwork from the shared illustration kit (all colour is tokenized). */
function NotificationsEmptyArt() {
  return (
    <Scene accent="blue" className="mx-auto h-[160px] w-[240px] max-w-full" title="No notifications">
      <Card x={44} y={26} w={152} h={100}>
        <Lines x={18} y={24} widths={[104, 118, 76]} />
        <Dots x={18} y={70} />
      </Card>
      <Card x={158} y={46} w={64} h={46} tone="accent" rotate={-6}>
        <Lines x={10} y={14} widths={[42, 28]} />
      </Card>
    </Scene>
  );
}

export function NotificationsPage() {
  const { items, unreadCount: unread, loading, error, refresh, canMarkRead, markRead, markAllRead } = useNotifications();
  const [filter, setFilter] = useState<Filter>("all");

  const visible = useMemo(
    () => (filter === "unread" ? items.filter((n) => !n.read) : items),
    [filter, items]
  );

  const filters: { id: Filter; label: string }[] = [
    { id: "all", label: `All (${items.length})` },
    { id: "unread", label: `Unread${unread > 0 ? ` (${unread})` : ""}` },
  ];

  return (
    <>
      <PageHeader
        title="Notifications"
        subtitle="Approvals, transfers, wallet activity, deployments and security alerts."
        actions={
          canMarkRead && <button
            type="button"
            onClick={() => { void markAllRead(); }}
            disabled={loading || unread === 0}
            className={cn(buttonVariants({ variant: "secondary", size: "sm" }), "disabled:opacity-50")}
          >
            Mark all as read
          </button>
        }
      />

      <div
        className="mb-4 flex items-center gap-1.5 overflow-x-auto"
        role="tablist"
        aria-label="Filter notifications"
      >
        {filters.map((f) => (
          <button
            key={f.id}
            type="button"
            role="tab"
            aria-selected={filter === f.id}
            onClick={() => setFilter(f.id)}
            className={cn(
              "min-h-9 shrink-0 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-semibold transition-colors",
              filter === f.id
                ? "bg-primary-soft text-primary"
                : "text-text hover:bg-bg"
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <LoadingState rows={5} />
      ) : error ? (
        <ApiFailure error={error} subject="your notifications" onRetry={() => refresh()} />
      ) : items.length === 0 ? (
        <div className="overflow-hidden rounded-xl border border-line bg-surface px-6 py-10 text-center">
          <NotificationsEmptyArt />
          <p className="mt-4 text-sm font-semibold text-ink">No notifications yet</p>
          <p className="mx-auto mt-1 max-w-[46ch] text-xs leading-snug text-muted">
            Alerts about approvals, transfers, wallets and deployments will appear here.
          </p>
        </div>
      ) : visible.length === 0 ? (
        <div className="overflow-hidden rounded-xl border border-line bg-surface">
          <NotificationEmptyState
            title="Nothing unread"
            description="You have read every notification. Switch to All to review the full history."
          />
        </div>
      ) : (
        <ul className="overflow-hidden rounded-xl border border-line bg-surface" data-testid="notification-list">
          {visible.map((n) => (
            <li key={n.id} className="border-b border-line last:border-b-0">
              <NotificationItem
                notification={n}
                readOnly={!canMarkRead}
                /* Activating a row reads it. A row that already carries a
                   subject (`href`) still navigates — `onActivate` runs
                   alongside the link, not instead of it. */
                onActivate={() => { if (canMarkRead && !n.read) void markRead(n.id); }}
              />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
